import { useEffect, useState } from "react";

/** Documented auth keys. This module reads them but never writes or clears them. */
export const AUTH_STORAGE_KEYS = [
  "token",
  "user_id",
  "user_name",
  "email",
  "access",
] as const;

export const SESSION_EXPIRED_EVENT = "mw-lounge-session-expired";
export const SESSION_EXPIRED_FLAG_KEY = "mw_lounge_session_expired";
export const SESSION_SYNC_KEY = "mw_lounge_session_sync";

/** Login-required copy from these two store endpoints — not a generic "unauthorized". */
const AUTH_FAILURE_TEXT =
  /unauthenticated|token(?:[_ ]+| is )?(expired|invalid)|please log ?in|please sign in|log in to see|login to view|sign in to see|need to log in|authorization token not found/i;

const AUTH_FAILURE_TEXT_MAX_LEN = 300;

let expiredInMemory = false;

export type AuthKeySnapshot = Record<(typeof AUTH_STORAGE_KEYS)[number], string | null>;

export const snapshotAuthKeys = (): AuthKeySnapshot => ({
  token: localStorage.getItem("token"),
  user_id: localStorage.getItem("user_id"),
  user_name: localStorage.getItem("user_name"),
  email: localStorage.getItem("email"),
  access: localStorage.getItem("access"),
});

const isRealToken = (token: string | null) =>
  Boolean(token) && token !== "null" && token !== "undefined";

export const hasLocalSessionKeys = () => isRealToken(localStorage.getItem("token"));

export const looksLikeHtmlBody = (value: unknown): boolean => {
  if (typeof value !== "string") {
    return false;
  }
  const head = value.trim().slice(0, 400).toLowerCase();
  return (
    head.startsWith("<") ||
    head.includes("<!doctype") ||
    head.includes("<html") ||
    head.includes("cf-browser-verification") ||
    head.includes("attention required") ||
    head.includes("cloudflare")
  );
};

const readExpiredFlag = () => {
  if (expiredInMemory) {
    return true;
  }
  try {
    return sessionStorage.getItem(SESSION_EXPIRED_FLAG_KEY) === "1";
  } catch {
    return false;
  }
};

const writeExpiredFlag = (expired: boolean) => {
  expiredInMemory = expired;
  try {
    if (expired) {
      sessionStorage.setItem(SESSION_EXPIRED_FLAG_KEY, "1");
    } else {
      sessionStorage.removeItem(SESSION_EXPIRED_FLAG_KEY);
    }
  } catch {
    /* sessionStorage may be unavailable */
  }
};

const emitSessionExpired = (expired: boolean, reason?: string) => {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, {
      detail: { expired, reason },
    })
  );
};

export const isSessionExpired = () =>
  hasLocalSessionKeys() && readExpiredFlag();

export const markSessionExpired = (reason?: string) => {
  if (!hasLocalSessionKeys() || readExpiredFlag()) {
    return;
  }
  writeExpiredFlag(true);
  emitSessionExpired(true, reason);
};

const broadcastSessionCleared = () => {
  try {
    localStorage.setItem(SESSION_SYNC_KEY, String(Date.now()));
  } catch {
    /* localStorage may be unavailable */
  }
};

export const clearSessionExpired = () => {
  if (!readExpiredFlag()) {
    return;
  }
  writeExpiredFlag(false);
  broadcastSessionCleared();
  emitSessionExpired(false);
};

/** Login/logout: drop this tab's flag and tell other tabs even if we were not expired. */
export const restoreLoungeSession = () => {
  writeExpiredFlag(false);
  broadcastSessionCleared();
  emitSessionExpired(false);
};

export const resetLoungeSessionForTests = () => {
  expiredInMemory = false;
  try {
    sessionStorage.removeItem(SESSION_EXPIRED_FLAG_KEY);
    localStorage.removeItem(SESSION_SYNC_KEY);
  } catch {
    /* ignore */
  }
};

export const resetLoungeSessionMemoryForTests = () => {
  expiredInMemory = false;
};

let sessionSyncInstalled = false;

const installLoungeSessionSync = () => {
  if (sessionSyncInstalled || typeof window === "undefined") {
    return;
  }
  sessionSyncInstalled = true;
  window.addEventListener("storage", (event) => {
    if (event.key === SESSION_SYNC_KEY) {
      if (readExpiredFlag()) {
        writeExpiredFlag(false);
        emitSessionExpired(false, "sync");
      }
      return;
    }
    if (event.key === "token") {
      if (isRealToken(event.newValue) && readExpiredFlag()) {
        writeExpiredFlag(false);
        emitSessionExpired(false, "storage");
      } else {
        emitSessionExpired(isSessionExpired(), "storage");
      }
      return;
    }
    if (event.key === null) {
      emitSessionExpired(isSessionExpired(), "storage");
    }
  });
};

installLoungeSessionSync();

const collectAuthText = (payload: unknown): string => {
  if (payload == null || looksLikeHtmlBody(payload)) {
    return "";
  }
  if (typeof payload === "string") {
    return payload.length > AUTH_FAILURE_TEXT_MAX_LEN ? "" : payload;
  }
  if (typeof payload !== "object") {
    return "";
  }
  const record = payload as Record<string, unknown>;
  const parts = [record.message, record.error, record.credit_balance, record.msg];
  return parts
    .map((part) => {
      if (typeof part === "string") {
        return part;
      }
      if (part && typeof part === "object") {
        const nested = part as { message?: unknown };
        return typeof nested.message === "string" ? nested.message : "";
      }
      return "";
    })
    .filter(Boolean)
    .join(" ");
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

const storeLikeRecord = (payload: unknown): Record<string, unknown> | null => {
  const record = asRecord(payload);
  if (!record) {
    return null;
  }
  const nested = asRecord(record.data);
  if (
    nested &&
    (Array.isArray(nested.store) ||
      typeof nested.credit_balance === "number" ||
      typeof nested.credit_balance === "string")
  ) {
    return nested;
  }
  if (Array.isArray(record.store) || "credit_balance" in record) {
    return record;
  }
  return null;
};

export const isAuthFailurePayload = (payload: unknown): boolean => {
  if (looksLikeHtmlBody(payload)) {
    return false;
  }
  const storeBody = storeLikeRecord(payload);
  if (storeBody) {
    return (
      typeof storeBody.credit_balance === "string" &&
      storeBody.credit_balance.length <= AUTH_FAILURE_TEXT_MAX_LEN &&
      AUTH_FAILURE_TEXT.test(storeBody.credit_balance)
    );
  }
  const text = collectAuthText(payload);
  if (text && text.length <= AUTH_FAILURE_TEXT_MAX_LEN && AUTH_FAILURE_TEXT.test(text)) {
    return true;
  }
  if (payload && typeof payload === "object") {
    const nested = (payload as { data?: unknown }).data;
    if (nested && nested !== payload) {
      return isAuthFailurePayload(nested);
    }
  }
  return false;
};

const axiosResponse = (error: unknown) =>
  (error as { response?: { status?: number; data?: unknown } })?.response;

export const isAxiosAuthFailure = (error: unknown): boolean => {
  if ((error as { isTransientRefreshFailure?: boolean })?.isTransientRefreshFailure) {
    return false;
  }
  if ((error as { isAuthFailure?: boolean })?.isAuthFailure) {
    return hasLocalSessionKeys();
  }
  if (!hasLocalSessionKeys()) {
    return false;
  }
  const response = axiosResponse(error);
  if (!response) {
    return false;
  }
  const status = response.status;
  if (status == null || looksLikeHtmlBody(response.data)) {
    return false;
  }
  if (status === 401) {
    if (response.data == null || response.data === "") {
      return true;
    }
    return isAuthFailurePayload(response.data);
  }
  if (status === 403) {
    return isAuthFailurePayload(response.data);
  }
  return false;
};

export const noteAuthFailureFromError = (error: unknown, reason?: string) => {
  if (isAxiosAuthFailure(error)) {
    markSessionExpired(reason);
    return true;
  }
  return false;
};

export const noteAuthFailureFromPayload = (payload: unknown, reason?: string) => {
  if (!hasLocalSessionKeys()) {
    return false;
  }
  if (isAuthFailurePayload(payload)) {
    markSessionExpired(reason);
    return true;
  }
  return false;
};

/** Shown after a stale-token mutation fails once. Refresh already ran; do not replay. */
export const LOUNGE_MUTATION_RETRY_MESSAGE =
  "Unable to complete that right now. Please try again.";

/** Session is valid but the endpoint still did not return a numeric balance. */
export const UNKNOWN_CREDIT_RETRY_MESSAGE =
  "Unable to load credits right now. Please try again.";

export const isKnownNumericCreditBalance = (
  value: unknown
): value is number => typeof value === "number" && Number.isFinite(value);

export const formatLoungeCreditBalance = (value: unknown): string => {
  if (isKnownNumericCreditBalance(value)) {
    return String(value);
  }
  return UNKNOWN_CREDIT_RETRY_MESSAGE;
};

const looksLikeAuthCopy = (value: unknown) =>
  Boolean(value) && isAuthFailurePayload(value);

/**
 * Map a mutation failure body to user-visible copy. Auth/jwt text becomes a
 * plain retry message so a renewed session is not shown as Sign in.
 * A dead refresh already marked the session expired — return null so callers
 * do not replace the Sign in prompt with an endless retry.
 */
export const userFacingMutationMessage = (
  payload: unknown,
  fallback: string = LOUNGE_MUTATION_RETRY_MESSAGE
): string | null => {
  if (isSessionExpired()) {
    return null;
  }
  if (looksLikeAuthCopy(payload)) {
    return fallback;
  }
  const record = asRecord(payload);
  const nested = record ? asRecord(record.data) : null;
  const raw =
    (typeof payload === "string" ? payload : "") ||
    (typeof record?.message === "string" ? record.message : "") ||
    (typeof record?.error === "string" ? record.error : "") ||
    (typeof record?.data === "string" ? record.data : "") ||
    (typeof nested?.message === "string" ? nested.message : "") ||
    (typeof nested?.error === "string" ? nested.error : "");
  if (!raw || looksLikeAuthCopy(raw) || looksLikeAuthCopy({ message: raw })) {
    return fallback;
  }
  return raw;
};

export const useLoungeSessionExpired = () => {
  const [expired, setExpired] = useState(isSessionExpired);

  useEffect(() => {
    const sync = () => setExpired(isSessionExpired());
    window.addEventListener(SESSION_EXPIRED_EVENT, sync);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, sync);
  }, []);

  return expired;
};
